/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { HtmlFhirInstanceGrouped } from '../algorithm/types';
import { fhirWidgetIdToRightPanelWidgetId } from './fhir_utils';

// Pixel offset from the middle of the widget
// This is to make room for more widgets
const WIDGET_OFFSET = 50;
/**
 *  This function is called when we resize the window, expand a document pearl icon,
 *  load new observation data etc.
 *
 *  It sets the y position of all the widgets to put them in the right place vertically.
 *  It tries to line the widget up with where it is contextualized, but without them
 *  overlapping.
 *
 *  It also sets the class 'collapsed-action' on widgets which are inside a collapsed action icon.
 *
 *  If two widgets correspond to the same predefined widget, they share a single item in
 *  HtmlFhirInstanceGrouped[] and their ids given in fhirWidgetIds[].  In this case, we want to
 *  align the sidebar widget with the first contextualized word that is visible.
 *  FIXME: Currently we just use the first of the fhirWidgetIds[] - fix this.
 *
 *  It's a many-to-many relationship between contextualized words and sidebar widgets.
 *  So if we have a single contextualized word which maps to two sidebar widgets,
 *  this will have two htmlFhir items, each with the same fhirWidgetIds[].
 *
 */
export function fhirUpdatePositions(
  htmlFhir: HtmlFhirInstanceGrouped[], // One entry per sidebar widget, with fhirWidgetIds[] giving the contextualized words
  messageIndex: number,
  messageContainer: HTMLElement | null | undefined,
  inEditorMode: boolean, // If true, we are editing a guide with ckeditor, so we want to show all widgets
  compact: boolean,
  showTopic: boolean,
  currentlyHighlightedWidgetIds: string[] | undefined // Make sure we don't hide the currently highlighted widget
): {
  firstOffsetTop: number | undefined;
  currentlyHighlightedTop: number | undefined;
} {
  const positions = [] as {
    n: HTMLElement;
    offsetTop: number;
    offsetMiddle: number;
    height: number;
    elementId: string;
    hidden: boolean;
    isCurrentlyHighlighted: boolean;
  }[];
  if (!messageContainer) {
    return { firstOffsetTop: undefined, currentlyHighlightedTop: undefined }; // Can happen when destroying the component
  }
  const positionTarget = inEditorMode ? undefined : getPositionTarget(messageContainer);
  const elementsById = indexElementsById(messageContainer, new Set(htmlFhir.flatMap((widget) => widget.fhirWidgetIds)));
  let currentlyHighlightedTop: number | undefined;
  htmlFhir.forEach((widget) => {
    const widgetId = widget.fhirWidgetIds[0]!; // Use the first of the html widgets (i.e. first contextualized word)

    // A tab change can render the new editor before the old editor's effect cleanup removes
    // its FHIR entries from Redux. Only position widgets found in the active message container.
    const n2 = findBestActionIcon(elementsById, widget.fhirWidgetIds);
    if (!n2) {
      return;
    }

    const sidebarId = fhirWidgetIdToRightPanelWidgetId(widgetId, widget.predefinedWidgetId, messageIndex, inEditorMode);
    // First get the height of the sidebar widget
    let n = document.getElementById(sidebarId);
    if (!n) {
      console.warn(
        'fhirUpdatePositions: could not find sidebar widget ' + sidebarId,
        'for predefined widget',
        widget.predefinedWidgetId
      );
      return;
    }
    const isCurrentlyHighlighted = !!currentlyHighlightedWidgetIds?.includes(widgetId);
    // Get the y position of the element.  This in widget.offsetTop but we still need to add
    // the position of the parent div, up to the container that includes the sidebar,
    // which is a div containing data-message-index
    // This n2 indicates the element in the parent container to which the current fhir widget corresponds
    // This sets the .collapsed-action class if we want to hide this widget because it's inside a collapsed action icon
    const { isInCollapsedActionIcon } = setActionListClasses(n2, n, isCurrentlyHighlighted, inEditorMode);
    if (inEditorMode) {
      n.style.removeProperty('margin-top');
      n.setAttribute('data-element-id', n2.id || n.id);
      n.classList.toggle('stacked-fhir-widget', !showTopic);
      if (isCurrentlyHighlighted) {
        currentlyHighlightedTop = n.offsetTop;
      }
      return;
    }
    const height = n.offsetHeight;
    if (height === 0) {
      //console.warn('sidebar widget has height 0', n.id);
      return; // Ignore this widget
    }
    let elementId = n2?.id || n.id;
    // recursively travel from the collapsed action to its parent and so on until we have no messages left
    const offsetMiddle = getOffsetRelativeToTarget(n2, positionTarget!).y;
    const offsetTop = -(height >> 1) + offsetMiddle - WIDGET_OFFSET;
    positions.push({
      n,
      offsetMiddle,
      offsetTop,
      height,
      elementId,
      hidden: isInCollapsedActionIcon && !inEditorMode,
      isCurrentlyHighlighted,
    });
  });
  if (inEditorMode) {
    return { firstOffsetTop: undefined, currentlyHighlightedTop };
  }
  positions.sort((a, b) => a.offsetMiddle - b.offsetMiddle);

  // Space between widgets
  const spacing = 0;
  let y = 0;
  const seenPredefinedWidgetNames: { [key: string]: { n: HTMLElement; y: number } } = {};
  positions.forEach(({ offsetTop, n, height, elementId, hidden, isCurrentlyHighlighted }) => {
    if (!compact && showTopic) {
      // In compact mode / when topic is hidden, we completely ignore the 'correct' position of the widgets, and just stack them
      // We still need the above code to get the order correct though
      y = Math.max(offsetTop, y);
    }

    const predefinedWidgetName = n.getAttribute('data-fhir-predefinedwidget');

    // if it is currently highlighted widget && already same widget exists
    // && existing widget is out of visible screen then display current highlighted widget
    if (isCurrentlyHighlighted && predefinedWidgetName && seenPredefinedWidgetNames[predefinedWidgetName]?.n) {
      const isExistingVisibleOnScreen = checkWidgetIsVisibleOnScreen(
        seenPredefinedWidgetNames[predefinedWidgetName]?.n!
      );
      if (!isExistingVisibleOnScreen) {
        delete seenPredefinedWidgetNames[predefinedWidgetName];
        // trigger event to force re-render fhir callout to display line correctly to widget
        window.dispatchEvent(new CustomEvent('fhirSidebarScroll'));
      }
    }

    if (!hidden && predefinedWidgetName) {
      seenPredefinedWidgetNames[predefinedWidgetName] = { n, y };
    }
    n.setAttribute('style', `margin-top: ${y}px;`);
    n.setAttribute('data-element-id', elementId);

    if (isCurrentlyHighlighted) {
      currentlyHighlightedTop = y;
    }
    n.classList.toggle('stacked-fhir-widget', !showTopic);
    // y += height + spacing;
    if (!hidden) {
      // We still place hidden widgets in the right place, but they are not visible and don't shift down the others.
      // This is so that they are in the right place if we need to temporary show them (e.g. when highlighting)
      y += height + spacing;
    }
  });
  return { firstOffsetTop: positions[0]?.offsetTop, currentlyHighlightedTop };
}

function getPositionTarget(messageContainer: HTMLElement | null | undefined): HTMLElement {
  const positionTarget = messageContainer?.closest('[data-fhir-position-root="true"]') as HTMLElement | null;
  if (!positionTarget) {
    throw new Error('fhirUpdatePositions: could not find [data-fhir-position-root="true"] for measurement');
  }
  return positionTarget;
}

function checkWidgetIsVisibleOnScreen(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return (
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
    rect.left < (window.innerWidth || document.documentElement.clientWidth)
  );
}
export function getMessageIndexForElement(element: HTMLElement): number {
  let parent = element.parentElement;
  while (parent) {
    const messageIndex = parent.getAttribute('data-message-index');
    if (messageIndex) {
      return parseInt(messageIndex, 10);
    }
    parent = parent.parentElement;
  }
  return -1;
}

export function getOffsetRelativeToTarget(element: HTMLElement | undefined, target: HTMLElement, fhirSpan = false) {
  let isInsideAction = false;
  while (
    // Find the nearest visible parent for the element
    element &&
    element !== target &&
    element.getBoundingClientRect().width === 0 &&
    element.getBoundingClientRect().height === 0
  ) {
    element = element.parentElement as HTMLElement;
    isInsideAction = fhirSpan && true;
  }

  if (!element || !target) return { x: 0, y: 0 };
  const elementRect = element.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();

  const x = elementRect.left - targetRect.left;
  const y = elementRect.top - targetRect.top + (isInsideAction ? elementRect.height + 3 : 0);

  return { x, y, isInsideAction };
}

/**
 * Find the first visible <action> element for the given <fhir id={widgetId}> element.  If none are visible, return the first.
 */
function findBestActionIcon(elementsById: Map<string, HTMLElement[]>, widgetIds: string[]) {
  const elements = widgetIds.flatMap((widgetId) => elementsById.get(widgetId) || []);
  for (const element of elements) {
    if (!checkIsInCollapsedActionIcon(element)) {
      return element;
    }
  }
  return elements[0];
}

function indexElementsById(container: HTMLElement | Document, targetIds: Set<string>): Map<string, HTMLElement[]> {
  const elementsById = new Map<string, HTMLElement[]>();
  container.querySelectorAll<HTMLElement>('[id]').forEach((element) => {
    if (!targetIds.has(element.id)) return;
    const matchingElements = elementsById.get(element.id) || [];
    matchingElements.push(element);
    elementsById.set(element.id, matchingElements);
  });
  return elementsById;
}

function checkIsInCollapsedActionIcon(parentElement: HTMLElement | null | undefined) {
  while (parentElement) {
    if (parentElement?.getAttribute('data-message-index') !== null || parentElement.id === 'htmlcontents-rendered')
      break;
    // n2 is a parent, which might be an icon (info etc)
    // Lets keep track of whether it is inside an info icon
    if (parentElement.nodeName === 'ACTION') {
      const actionType = parentElement.getAttribute('type');
      if (actionType !== null) {
        if (!parentElement.classList.contains('open')) {
          return true;
        }
      }
    }
    parentElement = parentElement.parentElement;
  }
  return false;
}

function setActionListClasses(
  parentElement: HTMLElement | null | undefined,
  element: HTMLElement,
  isCurrentlyHighlighted: boolean,
  inEditorMode: boolean
) {
  let isInCollapsedActionIcon = false;
  while (!isCurrentlyHighlighted && parentElement) {
    if (parentElement?.getAttribute('data-message-index') !== null || parentElement.id === 'htmlcontents-rendered')
      break;
    // n2 is a parent, which might be an icon (info etc)
    // Lets keep track of whether it is inside an info icon
    if (parentElement.nodeName === 'ACTION') {
      const actionType = parentElement.getAttribute('type');
      if (actionType !== null) {
        element.setAttribute('data-for-action', actionType);
        if (!parentElement.classList.contains('open')) {
          isInCollapsedActionIcon = true;
        }
      }
    }
    parentElement = parentElement.parentElement;
  }
  if (isInCollapsedActionIcon && !inEditorMode) {
    element.classList.add('collapsed-action');
  } else {
    element.classList.remove('collapsed-action');
  }
  return { isInCollapsedActionIcon };
}
